import {
  weeksBeforeContingentPackageExpireAlertShouldBeShown,
  weeksBeforeTrialPackageEndsAlertShouldBeShown,
} from '@common';
import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  AuthSelectors,
  DevicesSelectors,
  InstallationDetailsSelectors,
  InstallationType,
  RemoteConfigSelectors,
} from '@store';

import {
  AggregatedStatus,
  Device,
  DeviceRoles,
  InstallationItem,
  InstallationItemGateway,
  InstallationItemGatewayDevice,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@vi-iot/client';
import moment from 'moment';

import { AppState } from '../app.state';
import { BookingSelectors } from '../booking';
import { sortDevicesInTilesOrder } from '../devices/devices.helper';
import {
  GatewayStatusWithChangedAt,
  getDevicePlatformBasingOnRoles,
  getGatewayMainDevices,
  hasLegacyGateway,
  hasMainDevice,
  Platform,
} from '../installation-details';

import { InstallationPackageStatus } from './installations.model';
import { InstallationsState } from './installations.state';

export namespace InstallationsSelectors {
  export const heatingSystemsOverviewUrl =
    'https://systems-overview.viessmann-climatesolutions.com/details';

  export const installations = (state: AppState): InstallationsState => state.installations;

  export function selectInstallations(): MemoizedSelector<
    AppState,
    InstallationItem[],
    DefaultProjectorFn<InstallationItem[]>
  > {
    return createSelector(installations, (state: InstallationsState) => state.installations);
  }

  export function getLoadedIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(installations, (state: InstallationsState) => state.loaded);
  }

  export const areInstalltionsAndSubscriptionsLoaded = createSelector(
    installations,
    BookingSelectors.subscriptionsLoaded,
    (installationsState: InstallationsState, subscriptionsLoaded: boolean): boolean =>
      installationsState.loaded && subscriptionsLoaded
  );

  export function getIsDataIndicator(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(installations, (state: InstallationsState) => state.isData);
  }

  export function getState(): MemoizedSelector<
    AppState,
    InstallationsState,
    DefaultProjectorFn<InstallationsState>
  > {
    return createSelector(installations, (state: InstallationsState) => state);
  }

  export function getDeregisterInstallationState(): MemoizedSelector<
    AppState,
    any,
    DefaultProjectorFn<any>
  > {
    return createSelector(installations, (state: InstallationsState) => state.deregister);
  }

  export function getHeatingSystemOverviewUrl(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(DevicesSelectors.getSelectedDevice, (selectedDevice: Device) => {
      const equipmentNumber = selectedDevice?.equipmentNumber;

      if (equipmentNumber) {
        return `${this.heatingSystemsOverviewUrl}/${equipmentNumber}`;
      }

      return '';
    });
  }

  export function getInstallationPackage(
    boilerSerial: string
  ): MemoizedSelector<
    AppState,
    InstallationPackageStatus,
    DefaultProjectorFn<InstallationPackageStatus>
  > {
    return createSelector(
      BookingSelectors.getActiveSubscriptionsAsPackageStatus,
      RemoteConfigSelectors.isFlatRate,
      AuthSelectors.isCommercialUser,
      AuthSelectors.selectCompanySubscriptionPlan(),
      (
        subscriptions: InstallationPackageStatus[],
        isFlatRateActive,
        isCommercialUser,
        companyPlan
      ) => {
        // special case for flat rate users
        // some VIP customers were given flatRate although they don't belong to flatRate countries
        // https://jira.viessmann-climatesolutions.com/browse/VIOBS-1153
        if (companyPlan === SubscriptionPlan.Pro) {
          return {
            plan: SubscriptionPlan.Pro,
          };
        }

        const foundPackage: InstallationPackageStatus = subscriptions.find((entry) => {
          if (!isFlatRateActive && !isCommercialUser && boilerSerial === entry.boilerSerial) {
            return entry;
          }
          // For commercial with basic or pro package every installation is basic or pro and trial is not allowed without boiler serial
          if (
            isCommercialUser &&
            (entry.plan === SubscriptionPlan.Basic || entry.plan === SubscriptionPlan.Pro) &&
            (!entry.isTrial || entry.boilerSerial !== '')
          ) {
            return entry;
          }

          // For flat rate with pro package every installation is pro but it allows only one trial
          if (
            (isFlatRateActive &&
              entry.plan === SubscriptionPlan.Pro &&
              !entry.isTrial &&
              entry.boilerSerial === '') ||
            (isFlatRateActive && boilerSerial === entry.boilerSerial && entry.isTrial)
          ) {
            return entry;
          }
        });

        return foundPackage;
      }
    );
  }

  export function getLastStatusChangedAt(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string | null, DefaultProjectorFn<string | null>> {
    return createSelector(installations, (state: InstallationsState) => {
      for (let installation of state?.installations) {
        for (let gateway of installation.gateways) {
          if (gateway.serial === gatewaySerial) {
            return gateway?.lastStatusChangedAt;
          }
        }
      }

      return null;
    });
  }

  export function getAggregatedStatus(
    gatewaySerial: string
  ): MemoizedSelector<AppState, AggregatedStatus, DefaultProjectorFn<AggregatedStatus>> {
    return createSelector(installations, (state: InstallationsState) => {
      for (let installation of state?.installations) {
        for (let gateway of installation.gateways) {
          if (gateway.serial === gatewaySerial) {
            return gateway?.aggregatedStatus;
          }
        }
      }

      return null;
    });
  }

  export function getGatewayStatus(
    installationId: number,
    gatewaySerial?: string
  ): MemoizedSelector<
    AppState,
    GatewayStatusWithChangedAt,
    DefaultProjectorFn<GatewayStatusWithChangedAt>
  > {
    return createSelector(installations, (state: InstallationsState) => {
      const gateway: InstallationItemGateway = state?.installations
        ?.find((installation) => installation?.id === installationId)
        ?.gateways?.find((gw) => gw?.serial === gatewaySerial);

      const gatewayStatus: GatewayStatusWithChangedAt = {
        online:
          gateway?.aggregatedStatus === AggregatedStatus.WorksProperly ||
          gateway?.aggregatedStatus === AggregatedStatus.Error,
        offline: gateway?.aggregatedStatus === AggregatedStatus.Offline,
        error: gateway?.aggregatedStatus === AggregatedStatus.Error,
        maintenance: gateway?.aggregatedStatus === AggregatedStatus.Maintenance,
        remoteDiagnostic: gateway?.aggregatedStatus === AggregatedStatus.RemoteDiagnosticSession,
        worksProperly: gateway?.aggregatedStatus === AggregatedStatus.WorksProperly,
        nbiot: gateway?.aggregatedStatus === AggregatedStatus.NBIoT,
        lastStatusChangedAt: new Date(gateway?.lastStatusChangedAt).toLocaleString(),
      };

      return gatewayStatus;
    });
  }

  // get main devices from each gateway
  export function getInstallationMainDevices(
    installationId: number
  ): MemoizedSelector<
    AppState,
    InstallationItemGatewayDevice[],
    DefaultProjectorFn<InstallationItemGatewayDevice[]>
  > {
    return createSelector(installations, (state: InstallationsState) => {
      const gateways: InstallationItemGateway[] = state.installations.find(
        (installation) => installation.id === installationId
      ).gateways;

      return getGatewayMainDevices(gateways);
    });
  }

  export function selectMainDevicesAndOrderTiles(
    installationId: number
  ): MemoizedSelector<
    AppState,
    InstallationItemGatewayDevice[],
    DefaultProjectorFn<InstallationItemGatewayDevice[]>
  > {
    return createSelector(
      getInstallationMainDevices(installationId),
      (mainDevices: InstallationItemGatewayDevice[]) => {
        return sortDevicesInTilesOrder<InstallationItemGatewayDevice>(mainDevices);
      }
    );
  }

  export function isStandaloneGateway(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(installations, (state: InstallationsState) => {
      for (let installation of state?.installations) {
        for (let gateway of installation.gateways) {
          if (gateway.serial === gatewaySerial) {
            return !hasMainDevice(gateway) && hasLegacyGateway(gateway);
          }
        }
      }
    });
  }

  // When installation is selected it's more efficient to use getMainDevicePlatform from installation-details
  export function getPlatformTypeWithInstallationId(
    installationId: number
  ): MemoizedSelector<AppState, Platform, DefaultProjectorFn<Platform>> {
    return createSelector(
      getInstallationMainDevices(installationId),
      (mainDevice): Platform => {
        if (!mainDevice.length) {
          return Platform.None;
        }
        const platform = getDevicePlatformBasingOnRoles(mainDevice?.[0]?.roles);

        return platform;
      }
    );
  }

  export function getInstallationSubscriptionDetails(
    boilerSerial: string
  ): MemoizedSelector<
    AppState,
    InstallationPackageStatus,
    DefaultProjectorFn<InstallationPackageStatus>
  > {
    return createSelector(
      getInstallationPackage(boilerSerial),
      InstallationDetailsSelectors.selectInstallationSubscriptionPlan(boilerSerial),
      (installationPackageStatus, subscriptionPlan): InstallationPackageStatus => {
        const installationSubscriptionDetails: InstallationPackageStatus = {
          plan: installationPackageStatus?.plan || subscriptionPlan,
          isTrial: installationPackageStatus?.isTrial,
          boilerSerial,
        };

        return installationSubscriptionDetails.plan ? installationSubscriptionDetails : undefined;
      }
    );
  }

  export function showRemoteInspectionRecommendation(
    installationId: number
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(installations, (state: InstallationsState) => {
      const installationType = state.installations.find(
        (installation) => installation.id === installationId
      ).installationType;

      return installationType !== InstallationType.Commercial;
    });
  }

  export function showAlertBeforeTrialPackageEnds(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(BookingSelectors.selectSubscriptions(), (allPackages) => {
      const endDate = allPackages.find(
        (entry) =>
          entry.boilerId === boilerSerial &&
          entry.status?.toLowerCase() === SubscriptionStatus.active &&
          (entry.plan === SubscriptionPlan.Trial || entry.trial)
      )?.expirationDate;
      const dateAfterTwoWeeks = moment().add(
        weeksBeforeTrialPackageEndsAlertShouldBeShown,
        'weeks'
      );

      return endDate && moment(endDate).isBefore(dateAfterTwoWeeks);
    });
  }

  export function showAlertBeforeContingentPackageEnds(
    boilerSerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(BookingSelectors.selectSubscriptions(), (allPackages) => {
      const endDate = allPackages.find(
        (entry) =>
          entry.boilerId === boilerSerial &&
          entry.status?.toLowerCase() === SubscriptionStatus.active &&
          entry.plan === SubscriptionPlan.Pro &&
          entry.originalBundleInvoice
      )?.expirationDate;
      const dateAfterSixWeeks = moment().add(
        weeksBeforeContingentPackageExpireAlertShouldBeShown,
        'weeks'
      );

      return endDate && moment(endDate).isBefore(dateAfterSixWeeks);
    });
  }

  export function getActivePackageExpirationDate(
    boilerSerial: string
  ): MemoizedSelector<AppState, Date, DefaultProjectorFn<Date>> {
    return createSelector(BookingSelectors.selectSubscriptions(), (allPackages) => {
      const endDate = allPackages.find(
        (entry) =>
          entry.boilerId === boilerSerial &&
          entry.status?.toLowerCase() === SubscriptionStatus.active
      )?.expirationDate;

      return endDate;
    });
  }

  export function getAllDeviceSerialsByAnyMatchingRole(
    inputRoles: DeviceRoles[]
  ): MemoizedSelector<AppState, string[], DefaultProjectorFn<string[]>> {
    return createSelector(installations, (state: InstallationsState): string[] => {
      const boilerSerials: string[] = [];

      state.installations.forEach((installation) =>
        installation.gateways.forEach((gateway) =>
          gateway.devices.forEach((device) => {
            // ignore devices without boiler serial
            if (device?.serial && device.roles.some((role) => inputRoles.includes(role))) {
              boilerSerials.push(device.serial);
            }
          })
        )
      );

      return boilerSerials;
    });
  }

  export const selectNumberOfDisplayedInstallations = createSelector(
    installations,
    (state): number => state.numberOfInstallationsToDisplay
  );
}
